import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";

/**
 * 🔐 POST-SIGN-IN ACCESS GUARD
 * ----------------------------------------------------------------------------
 * Purpose:
 *   Enforces role-based access AFTER authentication and hydration are complete.
 *
 * Important:
 *   - This guard assumes the user has already passed the PostLoginHandoff.
 *   - It MUST NOT redirect while AuthProvider is still initializing.
 *   - It MUST NOT perform authentication logic.
 */
const PostSignInAccessGuard = ({ children, requiredRole }) => {
  const { user, matrix, isInitialising } = useAuth();

  // 1️⃣ Wait for AuthProvider to finish hydration
  //    (Critical: prevents redirect loops)
  if (isInitialising) {
    return (
      <div className="p-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3 text-muted">Verifying access…</p>
      </div>
    );
  }

  // 2️⃣ If no user exists at this point, session is invalid
  if (!user) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // 3️⃣ If no role restriction is specified, allow access
  if (!requiredRole) {
    return children;
  }

  // 4️⃣ Evaluate role membership
  const hasRole = Array.isArray(matrix)
    ? matrix.some(
        (m) =>
          String(m.roleCode || m.RoleCode)
            .toUpperCase() === String(requiredRole).toUpperCase()
      )
    : false;

  // 5️⃣ Deny access if role missing
  if (!hasRole) {
    console.warn(
      `🛑 ACCESS DENIED: Missing required role [${requiredRole}]`
    );
    return <Navigate to="/registered/dashboard" replace />;
  }

  // 6️⃣ Access granted
  return children;
};

export default PostSignInAccessGuard;
